import React from 'react';
import { useNavigate } from 'react-router-dom';
import NavbarChevronIcon from '../../layout/NavbarChevronIcon';
import DetailNavbarLeft from '../../shared/DetailNavbarLeft';
import NavbarCloseButton from '../../shared/NavbarCloseButton';
import { renderSkuWithSoftBreaks } from '../../../utils/skuSoftBreak';
import {
  DEFAULT_BUNDLE_WORK_FROM_PATH,
  findBundleWorkSiblingIndex,
  type BundleWorkNavState,
} from './bundleWorkNavState';

interface BundleWorkNavbarProps {
  readonly parentSku: string;
  readonly navState: BundleWorkNavState;
}

function BundleWorkNavbar({
  parentSku,
  navState,
}: BundleWorkNavbarProps): React.ReactElement {
  const navigate = useNavigate();
  const siblingIndex = findBundleWorkSiblingIndex(navState.siblingOrderIds, parentSku);
  const hasSiblings = navState.siblingOrderIds.length > 1 && siblingIndex >= 0;
  const prevSiblingSku =
    hasSiblings && siblingIndex > 0 ? navState.siblingOrderIds[siblingIndex - 1] : null;
  const nextSiblingSku =
    hasSiblings && siblingIndex < navState.siblingOrderIds.length - 1
      ? navState.siblingOrderIds[siblingIndex + 1]
      : null;

  const handleClose = (): void => {
    navigate(navState.fromPath || DEFAULT_BUNDLE_WORK_FROM_PATH);
  };

  const goToSibling = (sku: string): void => {
    navigate(`/bundle-work-order/${encodeURIComponent(sku)}`, {
      replace: true,
      state: {
        fromPath: navState.fromPath,
        siblingOrderIds: navState.siblingOrderIds,
      },
    });
  };

  return (
    <nav className="order-detail-navbar" aria-label="Bundle work order">
      <DetailNavbarLeft />
      <div className="order-detail-navbar-titles">
        <div className="order-detail-navbar-order-number">
          {renderSkuWithSoftBreaks(parentSku)}
        </div>
      </div>
      <div className="order-detail-navbar-right">
        <button
          type="button"
          className="order-detail-navbar-nav-btn"
          onClick={() => prevSiblingSku != null && goToSibling(prevSiblingSku)}
          disabled={prevSiblingSku == null}
          aria-label="Previous bundle work order"
        >
          <NavbarChevronIcon direction="left" />
        </button>
        <button
          type="button"
          className="order-detail-navbar-nav-btn"
          onClick={() => nextSiblingSku != null && goToSibling(nextSiblingSku)}
          disabled={nextSiblingSku == null}
          aria-label="Next bundle work order"
        >
          <NavbarChevronIcon direction="right" />
        </button>
        <NavbarCloseButton onClose={handleClose} />
      </div>
    </nav>
  );
}

export default BundleWorkNavbar;
